<template>
  <sub-page title="我的社保" @back="onPageBack">
    <!-- ============ 险种列表主视图 ============ -->
    <div class="social-main" v-if="view === 'main'">
      <!-- 蓝色个人信息横幅：姓名顶部左对齐 + ≡个人编号 -->
      <div class="info-banner">
        <span class="info-name">**杰</span>
        <span class="info-divider"></span>
        <!-- 个人编号列表图标：每行「一个点 + 一条横线」，共3行 -->
        <svg viewBox="0 0 24 24" class="info-list-icon" fill="#fff">
          <circle cx="2.8" cy="6" r="1.7"/>
          <rect x="7.2" y="5.15" width="14" height="1.7" rx="0.85"/>
          <circle cx="2.8" cy="12" r="1.7"/>
          <rect x="7.2" y="11.15" width="14" height="1.7" rx="0.85"/>
          <circle cx="2.8" cy="18" r="1.7"/>
          <rect x="7.2" y="17.15" width="14" height="1.7" rx="0.85"/>
        </svg>
        <span class="info-no">个人编号：1132752733</span>
      </div>

      <!-- 险种列表 -->
      <div class="ins-list">
        <div
          v-for="(ins, idx) in insuranceList"
          :key="ins.key"
          class="ins-item"
          :class="{ 'no-border': idx === insuranceList.length - 1 }"
          @click="openDetail(ins.key)"
        >
          <img :src="ins.icon" class="ins-icon" :alt="ins.name" />
          <span class="ins-name">{{ ins.name }}</span>
          <svg viewBox="0 0 24 24" class="ins-arrow">
            <path d="M9 5 L16 12 L9 19" fill="none" stroke="#B5B5B5" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      </div>
    </div>

    <!-- ============ 险种详情视图 ============ -->
    <div class="social-detail" :class="{ 'is-benefit': detailTab === 'benefit' }" v-else>
      <!-- 缴费 / 待遇发放 切换 -->
      <div class="detail-tabs">
        <div
          :class="['detail-tab', { active: detailTab === 'payment' }]"
          @click="detailTab = 'payment'"
        >缴费</div>
        <div
          :class="['detail-tab', { active: detailTab === 'benefit' }]"
          @click="detailTab = 'benefit'"
        >{{ benefitLabel }}</div>
      </div>

      <!-- 缴费列表 -->
      <template v-if="detailTab === 'payment'">
        <div class="month-card" v-for="(item, idx) in currentPayments" :key="idx" @click="toggleExpand(idx)">
          <!-- 月份 -->
          <div class="month">{{ item.month }}</div>

          <!-- 三列金额 -->
          <div class="amount-cols">
            <div class="amount-col">
              <div class="col-label">个人缴费</div>
              <div class="col-value">{{ fmt(item.personal) }}</div>
            </div>
            <div class="amount-col">
              <div class="col-label">单位缴纳</div>
              <div class="col-value">{{ fmt(item.company) }}</div>
            </div>
            <div class="amount-col">
              <div class="col-label">总金额</div>
              <div class="col-value">{{ fmt(item.total) }}</div>
            </div>
          </div>

          <!-- 展开明细 -->
          <template v-if="expanded[idx]">
            <div class="detail-row">
              <span class="detail-label">缴费基数</span>
              <span class="detail-value orange">{{ item.base }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">缴费标志</span>
              <span class="detail-value orange">{{ item.flag }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">参保单位</span>
              <span class="detail-value org">{{ maskOrg(item.org) }}</span>
            </div>
          </template>

          <!-- 展开/收起箭头：始终显示，展开朝上、收起朝下 -->
          <div class="expand-btn">
            <svg viewBox="0 0 24 24" class="expand-icon" :class="{ up: expanded[idx] }">
              <path d="M6 9 L12 15 L18 9" fill="none" stroke="#B5B5B5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>

        <!-- 空状态 -->
        <div class="empty-state" v-if="!currentPayments.length">
          <img :src="emptyImg" class="empty-img" alt="未查询到数据" />
          <div class="empty-text">未查询到数据</div>
        </div>
      </template>

      <!-- 待遇发放（暂无数据） -->
      <template v-if="detailTab === 'benefit'">
        <div class="empty-state">
          <img :src="emptyImg" class="empty-img" alt="未查询到数据" />
          <div class="empty-text">未查询到数据</div>
        </div>
      </template>
    </div>
  </sub-page>
</template>

<script>
import SubPage from './SubPage.vue'
// 缴费模拟数据（依据权益记录单，201609-202608 共120个月）：养老 8%+16%、工伤 0+0.2%、失业 0.5%+0.5%
import pensionPayments, { injuryPayments, unemployPayments } from '@/data/mySocialMock'

export default {
  name: 'MySocial',
  components: { SubPage },
  data() {
    return {
      // main：险种列表；detail：缴费/待遇明细
      view: 'main',
      detailTab: 'payment',
      currentKey: '',
      // 所有卡片默认收起
      expanded: [],
      emptyImg: require('@/assets/icons/social-empty.png'),
      insuranceList: [
        { key: 'pension', name: '企业养老保险', icon: require('@/assets/icons/social-pension.png') },
        { key: 'injury', name: '工伤保险', icon: require('@/assets/icons/social-injury.png') },
        { key: 'unemploy', name: '失业保险', icon: require('@/assets/icons/social-unemploy.png') }
      ],
      paymentMap: {
        pension: pensionPayments,
        injury: injuryPayments,
        unemploy: unemployPayments
      }
    }
  },
  computed: {
    currentPayments() {
      return this.paymentMap[this.currentKey] || []
    },
    // 待遇tab名称随险种变化（工伤待遇记录 / 失业发放记录）
    benefitLabel() {
      if (this.currentKey === 'injury') return '工伤待遇记录'
      if (this.currentKey === 'unemploy') return '失业发放记录'
      return '待遇发放'
    }
  },
  methods: {
    // 金额展示：去尾零（9.90→9.9、0→0），与线上权益单口径一致
    fmt(v) {
      return String(parseFloat(v.toFixed(2)))
    },
    // 参保单位打码：保留主体名称 + ******（与设计稿「南京国瑞能源******」口径一致）
    maskOrg(org) {
      return org.replace('有限公司', '') + '******'
    },
    openDetail(key) {
      this.currentKey = key
      this.detailTab = 'payment'
      this.expanded = []
      this.view = 'detail'
    },
    backToMain() {
      this.view = 'main'
    },
    // 详情视图按返回 → 回列表；列表视图按返回 → 路由返回
    onPageBack() {
      if (this.view === 'detail') {
        this.backToMain()
      } else {
        this.$router.back()
      }
    },
    toggleExpand(idx) {
      const list = this.expanded.slice()
      list[idx] = !list[idx]
      this.expanded = list
    }
  }
}
</script>

<style scoped>
/* ================= 主视图 ================= */
.social-main {
  min-height: 100%;
  flex: 1;
  background: #F5F6F7;
}
/* 蓝色信息横幅（青绿→蓝渐变，姓名/分隔条/个人编号同一水平线垂直居中） */
.info-banner {
  display: flex;
  align-items: center;
  height: calc(130 * var(--w));
  padding: 0 calc(24 * var(--w)) 0 calc(14 * var(--w));
  background: linear-gradient(90deg, #2AA8BE 0%, #3F92EF 100%);
}
.info-name {
  font-size: calc(40 * var(--w));
  line-height: 1;
  font-weight: bold;
  color: #fff;
}
.info-divider {
  width: calc(2 * var(--w));
  height: calc(36 * var(--w));
  background: rgba(255, 255, 255, 0.45);
  margin: 0 calc(20 * var(--w));
}
.info-list-icon {
  width: calc(30 * var(--w));
  height: calc(30 * var(--w));
  margin-right: calc(10 * var(--w));
}
.info-no {
  font-size: calc(28 * var(--w));
  color: #fff;
}

/* 险种列表 */
.ins-list {
  margin-top: calc(24 * var(--w));
  background: #fff;
}
.ins-item {
  position: relative;
  display: flex;
  align-items: center;
  height: calc(190 * var(--w));
  padding: 0 calc(48 * var(--w));
  border-bottom: 1px solid #F0F0F0;
  cursor: pointer;
}
.ins-item.no-border {
  border-bottom: none;
}
.ins-icon {
  width: calc(88 * var(--w));
  height: calc(88 * var(--w));
  object-fit: contain;
  margin-right: calc(36 * var(--w));
}
.ins-name {
  flex: 1;
  font-size: calc(44 * var(--w));
  color: #323233;
}
.ins-arrow {
  width: calc(36 * var(--w));
  height: calc(36 * var(--w));
}

/* ================= 详情视图 ================= */
/* 缴费/待遇发放统一背景色（与线上设计一致） */
.social-detail {
  min-height: 100%;
  flex: 1;
  background: #F5F5F5;
  display: flex;
  flex-direction: column;
}
/* 待遇类tab（待遇发放/工伤待遇记录/失业发放记录）为空状态页：
   内容区整页铺 #F8F8F8 */
.social-detail.is-benefit {
  background: #F8F8F8;
}
/* 顶部文字Tab */
.detail-tabs {
  display: flex;
  background: #fff;
  height: calc(150 * var(--w));
  flex-shrink: 0;
  border-bottom: 1px solid #F0F0F0;
}
.detail-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: calc(48 * var(--w));
  color: #666;
  border-bottom: calc(4 * var(--w)) solid transparent;
  cursor: pointer;
}
.detail-tab.active {
  color: #D88230;
  border-bottom-color: #D88230;
}

/* 月份卡片 */
.month-card {
  margin: calc(24 * var(--w)) calc(24 * var(--w)) 0;
  background: #fff;
  border-radius: calc(16 * var(--w));
  padding: calc(40 * var(--w)) calc(40 * var(--w)) calc(20 * var(--w));
}
.month {
  font-size: calc(48 * var(--w));
  font-weight: normal;
  color: #333;
}
/* 三列金额，列间浅竖线分隔 */
.amount-cols {
  display: flex;
  margin-top: calc(36 * var(--w));
}
.amount-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: calc(8 * var(--w)) 0;
}
.amount-col + .amount-col {
  border-left: 1px solid #F0F0F0;
}
.col-label {
  font-size: calc(40 * var(--w));
  color: #8A8A8A;
  line-height: 1.4;
}
.col-value {
  margin-top: calc(20 * var(--w));
  font-size: calc(48 * var(--w));
  color: #D88230;
  line-height: 1.2;
}

/* 展开明细行 */
.detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: calc(44 * var(--w));
}
.detail-label {
  font-size: calc(42 * var(--w));
  color: #8A8A8A;
}
.detail-value {
  font-size: calc(42 * var(--w));
}
.detail-value.orange {
  color: #D88230;
}
.detail-value.org {
  color: #666;
}

/* 展开/收起按钮 */
.expand-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(88 * var(--w));
  cursor: pointer;
}
.expand-icon {
  width: calc(44 * var(--w));
  height: calc(44 * var(--w));
  transition: transform 0.2s;
}
/* 展开状态：箭头旋转朝上 */
.expand-icon.up {
  transform: rotate(180deg);
}

/* 空状态 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: calc(420 * var(--w));
  background-color: #F8F8F8;

}
/* 待遇类空状态：撑满 tab 内容区剩余高度，使 #F8F8F8 铺到底部 */
.social-detail.is-benefit .empty-state {
  flex: 1;
  width: 100%;
}
.empty-img {
  width: calc(360 * var(--w));
  height: calc(300 * var(--w));
  object-fit: contain;
}
.empty-text {
  margin-top: calc(20 * var(--w));
  font-size: calc(40 * var(--w));
  color: #999;
}
</style>



// WEBPACK FOOTER //
// src/pages/MySocial.vue